import { BooleanField, DateTimeField, NumberField, TextField } from "@hex-insights/forms";
import { RefundFormValues } from "../../FormUtilities";
import { CurrencyField } from "../_General";
import { PaymentSelectField } from "../Payment";
import { BaseFieldProps } from "../shared";

/**
 * Generic props for fields of the Refund model.
 */
type FieldProps<K extends keyof RefundFormValues.Base = keyof RefundFormValues.Base> = BaseFieldProps<
	Pick<RefundFormValues.Base, K>
>;

/**
 * Field component for the `issueDate` field of the Refund model.
 */
export function IssueDate({ formState, ...props }: FieldProps<"issueDate">) {
	return <DateTimeField formState={formState} name="issueDate" precision="day" {...props} />;
}

/**
 * Field component for the `amount` field of the Refund model.
 */
export function Amount({ formState, ...props }: FieldProps<"amount">) {
	return <NumberField formState={formState} name="amount" validationUnit={1} min={0} {...props} />;
}

/**
 * Field component for the `localAmount` field of the Refund model.
 */
export function LocalAmount({ formState, ...props }: FieldProps<"localAmount">) {
	return <NumberField formState={formState} name="localAmount" optional validationUnit={1} min={0} {...props} />;
}

/**
 * Field component for the `isInLocalCurrency` field of the Refund model.
 */
export function IsInLocalCurrency({
	formState,
	label = "Paid with alternate currency",
	...props
}: FieldProps<"isInLocalCurrency">) {
	return <BooleanField formState={formState} name="isInLocalCurrency" label={label} {...props} />;
}

/**
 * Field component for the `localCurrency` field of the Refund model.
 */
export function LocalCurrency({ formState, ...props }: FieldProps<"localCurrency">) {
	return <CurrencyField formState={formState} name="localCurrency" {...props} />;
}

/**
 * Field component for the `notes` field of the Refund model.
 */
export function Notes({ formState, ...props }: FieldProps<"notes">) {
	return <TextField formState={formState} name="notes" multiLine {...props} />;
}

export namespace Payment {
	export type SelectProps = FieldProps<"paymentID"> &
		Omit<PaymentSelectField.SelectProps<RefundFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `payment` edge of the Refund model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PaymentSelectField.Select formState={formState} name="paymentID" {...props} />;
	}
}
